// ============================================================
// AmandaAvatar — global stationary concierge pin
// ============================================================
// Mounted once at App.js top-level (inside <BrowserRouter/>,
// outside <Routes/>) so a SINGLE instance follows the user
// across every route. This is intentionally NOT nested inside
// VerticalCockpit — global mount guarantees the pin renders on
// / (home), /training, /blueprint, /induction and every
// vertical without duplication.
//
// Palette-aware: on vertical routes the pulse and border pick
// up the current vertical's PRIMARY color (via /pathname lookup
// against PALETTES). On other routes, falls back to Amanda gold.
//
// Click behavior: opens a full-screen modal that plays
// VIDEOS.AMANDA_FOUNDER with the currently-visible module label
// as context (IntersectionObserver watches the 6 module anchors
// rendered by VerticalCockpit). On non-cockpit routes the modal
// just labels the walkthrough as "Wingman Alfred · Foyer".
// ============================================================
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X as XIcon } from "lucide-react";
import { VIDEOS, PALETTES } from "@/config/AssetSpine";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Alfred-provided Amanda headshot.
const AMANDA_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/be813b483ba52f65.jpeg";

// Palette key by route. Falls back to FOUNDERS (honey amber) on
// routes without a vertical skin.
const ROUTE_PALETTE = {
  "/green-shield":  "LAWN",
  "/uprising":      "MUSIC",
  "/moto":          "MOTO",
  "/sims":          "LOGISTICS",
  "/mobile-cycle":  "MOBILE_CYCLE",
  "/magic-garage":  "MAGIC_GARAGE",
  "/mixology":      "MIXOLOGY",
  "/msquared":      "MSQUARED",
  "/cierra":        "CIERRA",
  "/founders":      "FOUNDERS",
  "/affordable":    "AFFORDABLE",
};

// Human-readable vertical labels used to tag every Grid Briefing.
// Kept in sync with `verticalConfig.js` trade nouns.
const ROUTE_VERTICAL = {
  "/green-shield":  "Lawn Care",
  "/uprising":      "Live Music",
  "/moto":          "Motorcycle Service",
  "/sims":          "Fleet Logistics",
  "/mobile-cycle":  "Vintage Restoration",
  "/magic-garage":  "Garage Door Service",
  "/mixology":      "Craft Mixology",
  "/msquared":      "Masonry",
  "/cierra":        "Million Group Movement",
  "/founders":      "Founders Club",
  "/affordable":    "Property Management",
  "/jd-enterprises":"J&D Enterprises",
  "/blueprint":     "Rep Blueprint",
  "/training":      "Flight School",
  "/local":         "Local Node",
  "/demo":          "Magic Mirror",
};

const GOLD = "#FFD700";

// Route paths where we hide the pin entirely (auth-gated dashboards
// or the contract-signing screen where a second concierge would
// clutter the moment).
const HIDDEN_ROUTES = ["/login", "/signup", "/induction"];

const MODULE_LABELS = {
  greeting: "Greeting",
  shield:   "Shield",
  calendar: "Calendar",
  spine:    "Spine",
  billing:  "Billing",
  growth:   "Growth",
};

export default function AmandaAvatar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [activeModule, setActiveModule] = useState("greeting");
  const [briefing, setBriefing] = useState("");
  const [briefingStatus, setBriefingStatus] = useState("idle"); // idle | sending | received

  // Hard-hide on auth pages + the induction handshake.
  const hidden = HIDDEN_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`)
  );

  // Resolve palette (deep vertical routes tolerate trailing slugs).
  const paletteKey =
    Object.entries(ROUTE_PALETTE).find(([r]) => pathname === r || pathname.startsWith(`${r}/`))?.[1] ||
    "FOUNDERS";
  const P = PALETTES[paletteKey] || PALETTES.FOUNDERS;

  // IntersectionObserver: watch the six cockpit anchor sections.
  // On non-cockpit routes the query yields zero nodes → observer
  // is a no-op, activeModule stays at its initial value.
  useEffect(() => {
    if (hidden) return undefined;
    const ids = Object.keys(MODULE_LABELS);
    const nodes = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (nodes.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.getAttribute("id");
          if (id) setActiveModule(id);
        }
      },
      { rootMargin: "-30% 0px -40% 0px", threshold: [0.1, 0.45, 0.8] }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [pathname, hidden]);

  // Escape-to-close + body-scroll-lock while modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (hidden) return null;

  const onCockpit = paletteKey !== "FOUNDERS" || pathname === "/founders";
  const contextLabel = onCockpit && MODULE_LABELS[activeModule]
    ? `Module: ${MODULE_LABELS[activeModule]}`
    : "Wingman Alfred · Foyer";

  // Resolve the vertical tag for this route so every briefing lands
  // in the DB with an industry stamp (Lawn Care, Live Music, …).
  const routeMatch = Object.entries(ROUTE_VERTICAL).find(
    ([r]) => pathname === r || pathname.startsWith(`${r}/`)
  );
  const verticalLabel = routeMatch ? routeMatch[1] : "Grid Lobby";

  const submitBriefing = () => {
    const msg = briefing.trim();
    if (!msg || briefingStatus === "sending") return;
    setBriefingStatus("sending");
    // Truly fire-and-forget — the pop-over settles into "received"
    // the instant the browser accepts the request. A slow or failing
    // network never blocks the operator; the server is idempotent
    // and any transient failure is logged, not surfaced.
    const payload = {
      message: msg,
      vertical: verticalLabel,
      route: pathname,
      module: onCockpit ? MODULE_LABELS[activeModule] || null : null,
    };
    fetch(`${API_BASE}/api/grid-briefing`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* swallowed intentionally */ });

    // Settle UI immediately — do not await the network.
    setBriefing("");
    setBriefingStatus("received");
    setTimeout(() => setBriefingStatus("idle"), 4000);
  };

  return (
    <>
      {/* Scoped pulse keyframes tinted by the current palette */}
      <style>{`
        @keyframes amanda-avatar-pulse {
          0%   { box-shadow: 0 0 0 0 ${P.PRIMARY}B0; }
          70%  { box-shadow: 0 0 0 20px ${P.PRIMARY}00; }
          100% { box-shadow: 0 0 0 0 ${P.PRIMARY}00; }
        }
      `}</style>

      {/* Pin */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open Amanda walkthrough"
        data-testid="amanda-avatar"
        title="Click to open Amanda's walkthrough"
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 9999,
          width: "72px",
          height: "72px",
          borderRadius: "50%",
          padding: 0,
          border: `3px solid ${P.PRIMARY}`,
          background: `url(${AMANDA_IMG}) center/cover no-repeat, #1a1a1a`,
          cursor: "pointer",
          animation: "amanda-avatar-pulse 2.2s ease-out infinite",
        }}
      />

      {/* Modal */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Amanda walkthrough"
          data-testid="amanda-avatar-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(4, 6, 10, 0.82)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "960px",
              background: P.BG_HI || "#111",
              border: `1px solid ${P.PRIMARY}88`,
              borderRadius: "6px",
              overflow: "hidden",
              boxShadow: `0 40px 80px -20px ${P.PRIMARY}44`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                padding: "16px 20px",
                borderBottom: `1px solid ${P.PRIMARY}33`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    color: P.MUTED || "#888",
                  }}
                  data-testid="amanda-avatar-modal-kicker"
                >
                  Amanda is walking you through
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: P.INK || "#fff",
                  }}
                  data-testid="amanda-avatar-modal-title"
                >
                  {contextLabel}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Amanda walkthrough"
                data-testid="amanda-avatar-modal-close"
                style={{
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  background: `${P.PRIMARY}18`,
                  border: `1px solid ${P.PRIMARY}55`,
                  color: P.INK || "#fff",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                <XIcon size={18} />
              </button>
            </div>

            <video
              src={VIDEOS.AMANDA_FOUNDER}
              autoPlay
              controls
              playsInline
              preload="metadata"
              style={{
                display: "block",
                width: "100%",
                aspectRatio: "16 / 9",
                background: "#000",
              }}
              data-testid="amanda-avatar-modal-video"
            />

            {/* Grid Briefing — text intake tagged with the current vertical */}
            <div
              style={{
                padding: "18px 20px 20px",
                borderTop: `1px solid ${P.PRIMARY}22`,
                background: `linear-gradient(180deg, ${P.PRIMARY}0A 0%, transparent 100%)`,
              }}
              data-testid="grid-briefing-block"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <label
                  htmlFor="grid-briefing-input"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    color: GOLD,
                    fontWeight: 700,
                  }}
                  data-testid="grid-briefing-label"
                >
                  Grid Briefing
                </label>
                <span
                  style={{
                    fontSize: 9,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    color: P.MUTED || "#888",
                  }}
                  data-testid="grid-briefing-vertical-tag"
                >
                  Tag · {verticalLabel}
                </span>
              </div>

              <textarea
                id="grid-briefing-input"
                data-testid="grid-briefing-input"
                value={briefing}
                onChange={(e) => setBriefing(e.target.value)}
                placeholder="Amanda, I need help with…"
                rows={3}
                disabled={briefingStatus === "sending"}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  background: "rgba(0,0,0,0.35)",
                  border: `1px solid ${P.PRIMARY}44`,
                  borderRadius: 4,
                  color: P.INK || "#fff",
                  fontSize: 14,
                  lineHeight: 1.4,
                  resize: "vertical",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: briefingStatus === "received" ? GOLD : (P.MUTED || "#888"),
                    fontWeight: briefingStatus === "received" ? 600 : 400,
                    minHeight: 18,
                  }}
                  data-testid="grid-briefing-status"
                >
                  {briefingStatus === "received"
                    ? "Briefing Received. The Spine is being updated."
                    : "Brief Amanda on a logic fix or training need."}
                </div>
                <button
                  type="button"
                  onClick={submitBriefing}
                  disabled={!briefing.trim() || briefingStatus === "sending"}
                  data-testid="grid-briefing-submit"
                  style={{
                    padding: "10px 18px",
                    background: GOLD,
                    color: "#0A0A0A",
                    border: "none",
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    cursor: (!briefing.trim() || briefingStatus === "sending") ? "not-allowed" : "pointer",
                    opacity: (!briefing.trim() || briefingStatus === "sending") ? 0.55 : 1,
                    boxShadow: `0 4px 14px -4px ${GOLD}66`,
                  }}
                >
                  {briefingStatus === "sending" ? "Sending…" : "Send Briefing"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
